import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Download,
  ArrowLeft,
  Zap,
  Shield,
  DollarSign,
  Users,
  Clock,
  CheckCircle2,
  Building2,
  Phone,
  Mail,
  Globe,
  Award,
  Truck,
  Wrench,
  MapPin,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_isaac-platform/artifacts/9qsxumik_Sterile%20Mate%20Logo%20%282%29.jpg";
const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";

export default function SalesOnePagerPage() {
  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-content, .print-content * { visibility: visible; }
          .print-content { 
            position: absolute; 
            left: 0; 
            top: 0; 
            width: 100%;
            padding: 0;
            margin: 0;
          }
          .no-print { display: none !important; }
          @page { 
            size: letter portrait; 
            margin: 0.5in; 
          }
          .print-content {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>

      {/* Navigation Header - No Print */}
      <div className="no-print bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <Link to="/investor" className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to Investor Portal</span>
            </Link>
            <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Printable One-Pager Content */}
      <div className="print-content bg-white min-h-screen">
        <div className="max-w-4xl mx-auto p-8">
          
          {/* Header */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-cyan-500">
            <div className="flex items-center gap-4">
              <img src={ASKISAAC_LOGO} alt="AskIsaac" className="w-14 h-14 rounded-xl" />
              <div>
                <h1 className="text-2xl font-bold text-slate-900">AskIsaac.io</h1>
                <p className="text-cyan-600 font-medium">On-Demand Healthcare Operations</p>
              </div>
            </div>
            <img src={LOGO_URL} alt="Sterile Mate" className="h-12" />
          </div>

          {/* Tagline */}
          <div className="bg-gradient-to-r from-slate-900 to-cyan-900 text-white rounded-xl p-6 mb-6 text-center">
            <h2 className="text-xl font-bold mb-2">One Platform. Supplies. Maintenance. On Demand.</h2>
            <p className="text-cyan-200">Expand your network with verified suppliers and contractors — competitive bids in minutes</p>
          </div>

          {/* Problem / Solution Grid */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* The Challenge */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
              <h3 className="text-lg font-bold text-amber-800 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm">⚡</span>
                Healthcare Supply Challenges
              </h3>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">⚡</span>
                  <span>Supply chain disruptions need backup options</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">⚡</span>
                  <span>Equipment repairs can take weeks to schedule</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">⚡</span>
                  <span>Limited visibility on contractor arrival</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">⚡</span>
                  <span>Manual 3-quote process causes delays</span>
                </li>
              </ul>
            </div>

            {/* The Solution */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
              <h3 className="text-lg font-bold text-emerald-800 mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                The AskIsaac Model
              </h3>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  <span><strong>Competitive bidding</strong> — best price wins</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  <span><strong>Live GPS tracking</strong> — verified fulfillment</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  <span><strong>STAT Response</strong> — 2-hour emergency tier</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  <span><strong>Built-in compliance</strong> — audit-ready</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Dual Revenue Model */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Truck className="w-6 h-6 text-amber-600" />
                <h4 className="font-bold text-amber-800">Supply Network</h4>
              </div>
              <p className="text-sm text-slate-700 mb-2">Extended supplier network with competitive pricing</p>
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-amber-600">35%</span>
                <span className="text-xs text-slate-500">Take Rate</span>
              </div>
            </div>
            <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Wrench className="w-6 h-6 text-emerald-600" />
                <h4 className="font-bold text-emerald-800">Service Network</h4>
              </div>
              <p className="text-sm text-slate-700 mb-2">Maintenance, repairs, PM, project management</p>
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-emerald-600">44%</span>
                <span className="text-xs text-slate-500">Take Rate</span>
              </div>
            </div>
          </div>

          {/* Response Tiers */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-6">
            <h3 className="text-lg font-bold text-slate-800 mb-4">Response Tiers</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white border border-blue-200 rounded-lg p-3 text-center">
                <Clock className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                <p className="font-bold text-blue-800">Standard</p>
                <p className="text-lg font-semibold text-slate-700">24-48 hrs</p>
                <p className="text-xs text-slate-500">Routine requests</p>
              </div>
              <div className="bg-white border border-amber-200 rounded-lg p-3 text-center">
                <Zap className="w-6 h-6 text-amber-600 mx-auto mb-1" />
                <p className="font-bold text-amber-800">Priority</p>
                <p className="text-lg font-semibold text-slate-700">4-8 hrs</p>
                <p className="text-xs text-slate-500">Urgent needs</p>
              </div>
              <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3 text-center">
                <AlertTriangle className="w-6 h-6 text-red-600 mx-auto mb-1" />
                <p className="font-bold text-red-800">STAT</p>
                <p className="text-lg font-semibold text-slate-700">2 hrs</p>
                <p className="text-xs text-red-600 font-medium">OR/ER Emergency</p>
              </div>
            </div>
          </div>

          {/* How It Works */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-slate-800 mb-4 text-center">How It Works</h3>
            <div className="flex items-center justify-between">
              {[
                { step: "1", title: "Post", desc: "Hospital submits need" },
                { step: "2", title: "Ping", desc: "Network notified" },
                { step: "3", title: "Bid", desc: "Vendors compete blindly" },
                { step: "4", title: "Track", desc: "GPS verification" }
              ].map((item, i) => (
                <div key={i} className="flex items-center">
                  <div className="text-center">
                    <div className="w-10 h-10 rounded-full bg-cyan-600 text-white font-bold flex items-center justify-center mx-auto mb-1">
                      {item.step}
                    </div>
                    <p className="font-semibold text-sm text-slate-800">{item.title}</p>
                    <p className="text-xs text-slate-500">{item.desc}</p>
                  </div>
                  {i < 3 && <div className="w-12 h-0.5 bg-cyan-300 mx-2" />}
                </div>
              ))}
            </div>
          </div>

          {/* Key Differentiators */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
              <Users className="w-6 h-6 text-blue-600 mx-auto mb-1" />
              <p className="text-xs font-semibold text-slate-700">Anonymous Bidding</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
              <MapPin className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
              <p className="text-xs font-semibold text-slate-700">Live GPS Tracking</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
              <Shield className="w-6 h-6 text-amber-600 mx-auto mb-1" />
              <p className="text-xs font-semibold text-slate-700">Built-in Compliance</p>
            </div>
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-center">
              <Award className="w-6 h-6 text-violet-600 mx-auto mb-1" />
              <p className="text-xs font-semibold text-slate-700">AAMI ST108 Ready</p>
            </div>
          </div>

          {/* Contact Footer */}
          <div className="bg-slate-900 text-white rounded-xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg mb-1">Ready to mobilize your network?</h3>
                <p className="text-slate-400 text-sm">Schedule a demo today</p>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-cyan-400" />
                  <span>800.988.3892</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-cyan-400" />
                  <span>info@sterilemate.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-cyan-400" />
                  <span>sterilemate.com</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
